import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl, FormattedMessage } from 'react-intl';
import get from 'lodash/get';
import camelcase from 'lodash/camelCase';

import Tab from '../../../components/Tab';
import './styles.css';

import withHostContext, {
  trackClickTypes,
  trackingLocations,
  trackView,
  trackEventGa,
} from '../../../context/host/consumer';
import DeadlinesTab from './DeadlinesTab';
import ImpPoliciesTab from './ImpPoliciesTab';
import FeesTab from './FeesTab';

const messages = defineMessages({
  title: {
    id: 'hubs.admissions.application-info.title',
    defaultMessage: 'Application Information',
  },
  deadlines: {
    id: 'hubs.admissions.application-info.deadlines',
    defaultMessage: 'Deadlines',
    clickId: 'deadlines',
  },
  importantPolicies: {
    id: 'hubs.admissions.application-info.imp-policies',
    defaultMessage: 'Important Policies',
    clickId: 'policies',
  },
  fees: {
    id: 'hubs.admissions.application-info.fees',
    defaultMessage: 'Fees',
    clickId: 'fees',
  },
  footer: {
    id: 'hubs.admissions.application-info.footer',
    defaultMessage: 'Latest available data from',
  },
});

class ApplicationInformation extends React.PureComponent {
  static propTypes = {
    admissionsData: PropTypes.object,
    deliveryType: PropTypes.string,
    isApplyingTo: PropTypes.bool,
    websiteUrl: PropTypes.string,
    isCommonAppDeadlinesFlagOn: PropTypes.bool,
  };

  constructor(props) {
    super(props);

    this.state = {
      activeIndex: 0,
    };
  }

  handleTabChange = (e, { activeIndex }) => {
    const panes = this.getPanes(null, activeIndex);
    const tabClickId = panes[activeIndex].menuItem.key;
    const label = `admissions-information-${tabClickId}`;
    const location = `${trackingLocations.TABS.ADMISSIONS}_${
      trackingLocations.TABS.TABS
    }_${camelcase(tabClickId)}`;
    trackView(this.props.hostContext, {
      type: trackClickTypes.HUBS_VIEW,
      location,
    });
    trackEventGa(this.props.hostContext, {
      type: 'click',
      category: 'Hubs Tabs',
      label,
    });
    this.setState({
      activeIndex,
    });
  };

  getMenuItem = (selected, message) => {
    return {
      content: this.props.intl.formatMessage(message),
      key: message.clickId,
      role: 'tab',
      'aria-label': this.props.intl.formatMessage(message),
      'aria-selected': selected,
      className: 'tuition-tab-item',
      tabIndex: 0,
    };
  };

  isValidFeesData = () => {
    const { admissionsData } = this.props;
    if (
      get(admissionsData, 'fees.applicationFee.value') ||
      get(admissionsData, 'fees.deposit.value')
    ) {
      return true;
    }
    return false;
  };

  getPanes = () => {
    const { activeIndex } = this.state;
    const {
      deliveryType,
      admissionsData,
      isApplyingTo,
      websiteUrl,
      isCommonAppDeadlinesFlagOn,
    } = this.props;
    const panes = [];
    let index = 0;

    const deadlines = get(admissionsData, 'deadlines', null);
    if (deadlines && deadlines.length > 0) {
      if (deadlines.find((deadline) => !!deadline)) {
        panes.push({
          menuItem: this.getMenuItem(activeIndex === index++, messages.deadlines),
          render: () => (
            <DeadlinesTab
              deadlines={deadlines}
              isCommonAppDeadlinesFlagOn={isCommonAppDeadlinesFlagOn}
            />
          ),
        });
      }
    }

    const impPolicies = get(admissionsData, 'importantPolicies', null);
    if (deliveryType && impPolicies && impPolicies.admissionsPolicy) {
      panes.push({
        menuItem: this.getMenuItem(activeIndex === index++, messages.importantPolicies),
        render: () => (
          <ImpPoliciesTab
            importantPolicies={impPolicies}
            deliveryType={deliveryType}
            isApplyingTo={isApplyingTo}
          />
        ),
      });
    }

    if (this.isValidFeesData()) {
      panes.push({
        menuItem: this.getMenuItem(activeIndex === index++, messages.fees),
        render: () => (
          <FeesTab fees={get(admissionsData, 'fees', {})} websiteUrl={websiteUrl} />
        ),
      });
    }

    return panes;
  };

  render() {
    const { activeIndex } = this.state;
    const panes = this.getPanes();
    return panes && panes.length > 0 ? (
      <div
        name="application-info-deadlines"
        className="application-info-container common-full-width"
        data-cy="application-info-container"
      >
        <div className="common-core-width">
          <h2 tabIndex={0}>
            <FormattedMessage {...messages.title} />
          </h2>
          <div className="tabs-container">
            <Tab
              menu={{
                secondary: true,
                pointing: true,
                role: 'tablist',
              }}
              panes={panes}
              onTabChange={this.handleTabChange}
              activeIndex={activeIndex}
            />
          </div>
        </div>
      </div>
    ) : null;
  }
}

export default injectIntl(withHostContext(ApplicationInformation));
